import { useState } from 'react';
import { Link, useMatchRoute, useRouterState } from '@tanstack/react-router';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
  SidebarLanguageToggle,
  useSidebar,
} from '@org/ui';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from '@org/ui';
import {
  Users,
  UsersRound,
  Map,
  Waypoints,
  ShoppingBag,
  Layers,
  BookHeart,
  BookOpen,
  CalendarDays,
  MessageSquareText,
  Megaphone,
  GraduationCap,
  BarChart3,
  Clapperboard,
  Mic,
  ChevronRight,
  Library,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';

type NavigationItem = {
  labelKey: string;
  href: string;
  icon: LucideIcon;
  requiredPermissions?: string[];
};

type NavigationGroup = {
  labelKey: string;
  icon: LucideIcon;
  children: NavigationItem[];
};

type NavigationEntry = NavigationItem | NavigationGroup;

const isGroup = (entry: NavigationEntry): entry is NavigationGroup => 'children' in entry;

export const adminNavigationItems: NavigationEntry[] = [
  {
    labelKey: 'sidebar.sections.students',
    icon: Users,
    children: [
      { labelKey: 'sidebar.navigation.students', href: '/students', icon: Users, requiredPermissions: ['admin:all'] },
      { labelKey: 'sidebar.navigation.groups', href: '/groups', icon: UsersRound, requiredPermissions: ['admin:all'] },
      { labelKey: 'sidebar.navigation.identityReviews', href: '/identity-reviews', icon: ShieldCheck, requiredPermissions: ['admin:all', 'users:write'] },
    ],
  },
  {
    labelKey: 'sidebar.sections.content',
    icon: Library,
    children: [
      { labelKey: 'sidebar.navigation.journeys', href: '/journeys', icon: Map, requiredPermissions: ['admin:all'] },
      { labelKey: 'sidebar.navigation.learningPaths', href: '/learning-paths', icon: Waypoints, requiredPermissions: ['admin:all'] },
      { labelKey: 'sidebar.navigation.products', href: '/products', icon: ShoppingBag, requiredPermissions: ['admin:all', 'products:*'] },
      { labelKey: 'sidebar.navigation.units', href: '/units', icon: Layers, requiredPermissions: ['classes:write', 'admin:all'] },
      { labelKey: 'sidebar.navigation.women', href: '/women', icon: BookHeart, requiredPermissions: ['admin:all'] },
      { labelKey: 'sidebar.navigation.classes', href: '/classes', icon: BookOpen, requiredPermissions: ['classes:write', 'admin:all'] },
      { labelKey: 'sidebar.navigation.abilities', href: '/abilities', icon: GraduationCap, requiredPermissions: ['levels:write', 'admin:all'] },
      { labelKey: 'sidebar.navigation.grammarVideos', href: '/grammar-videos', icon: Clapperboard, requiredPermissions: ['admin:all'] },
      { labelKey: 'sidebar.navigation.conversationTopics', href: '/conversation-topics', icon: Mic, requiredPermissions: ['admin:all'] },
    ],
  },
  { labelKey: 'sidebar.navigation.calendar', href: '/events', icon: CalendarDays, requiredPermissions: ['events:read', 'admin:all'] },
  { labelKey: 'sidebar.navigation.feedback', href: '/feedback', icon: MessageSquareText, requiredPermissions: ['admin:all'] },
  { labelKey: 'sidebar.navigation.announcements', href: '/announcements', icon: Megaphone, requiredPermissions: ['admin:all'] },
  { labelKey: 'sidebar.navigation.analytics', href: '/analytics', icon: BarChart3, requiredPermissions: ['admin:all', 'analytics:read'] },
];

/** Keeps the entries the user may see; a group shows only its visible children and disappears when none are left. */
export const filterNavigation = (
  entries: NavigationEntry[],
  canSee: (item: NavigationItem) => boolean,
): NavigationEntry[] =>
  entries.flatMap((entry) => {
    if (!isGroup(entry)) return canSee(entry) ? [entry] : [];
    const children = entry.children.filter(canSee);
    return children.length ? [{ ...entry, children }] : [];
  });

function NavigationGroupItem({
  group,
  isItemActive,
  onNavigate,
}: {
  group: NavigationGroup;
  isItemActive: (href: string) => boolean;
  onNavigate: () => void;
}) {
  const { t } = useTranslation('admin');
  const hasActiveChild = group.children.some((child) => isItemActive(child.href));
  const [expanded, setExpanded] = useState(hasActiveChild);
  const Icon = group.icon;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={hasActiveChild && !expanded}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        className="rounded-lg"
      >
        <Icon />
        <span>{t(group.labelKey)}</span>
        <ChevronRight className={`ml-auto transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </SidebarMenuButton>
      {expanded && (
        <SidebarMenuSub>
          {group.children.map((child) => {
            const ChildIcon = child.icon;
            return (
              <SidebarMenuSubItem key={child.href}>
                <SidebarMenuSubButton asChild isActive={isItemActive(child.href)}>
                  <Link to={child.href} onClick={onNavigate}>
                    <ChildIcon />
                    <span>{t(child.labelKey)}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            );
          })}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

export function AdminSidebar() {
  const matchRoute = useMatchRoute();
  const routerState = useRouterState();
  const { isMobile, setOpen, setOpenMobile, toggleSidebar } = useSidebar();
  const { t } = useTranslation('admin');
  const { hasAnyPermission, isLoading } = useAuthorization();
  const visibleItems = isLoading
    ? []
    : filterNavigation(adminNavigationItems, item =>
        !item.requiredPermissions?.length || hasAnyPermission(item.requiredPermissions)
      );

  const isItemActive = (href: string) =>
    !!matchRoute({ to: href, fuzzy: href === '/' }) &&
    routerState.location.pathname.startsWith(href);

  const closeSidebar = () => {
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <Sidebar className="border-r border-rose-100 bg-white/90" collapsible="offcanvas">
      <SidebarHeader className="flex flex-row items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={t('common.nav.menu')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#cf2e2e] shadow-sm"
          >
            <img src="/icon-hand-small-white.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
          </button>
          <span className="text-base font-semibold text-gray-900">English2Empower</span>
        </div>
        <SidebarTrigger className="text-[#cf2e2e]" />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('common.nav.menu')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => {
                if (isGroup(item)) {
                  return (
                    <NavigationGroupItem
                      key={item.labelKey}
                      group={item}
                      isItemActive={isItemActive}
                      onNavigate={closeSidebar}
                    />
                  );
                }
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={isItemActive(item.href)} className="rounded-lg">
                      <Link to={item.href} onClick={closeSidebar}>
                        <Icon />
                        <span>{t(item.labelKey)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-3">
        <SidebarLanguageToggle />
      </SidebarFooter>
      <SidebarRail className="hidden md:flex" />
    </Sidebar>
  );
}